
import {
  defineComponent,
  onBeforeMount,
  ref,
} from 'vue';
import { useI18n } from 'vue-i18n';
import SpmTable from '@/components/table/SpmTable.vue';
import {
  SpmTableAction, SpmTableColumns, SpmTableState, SpmTableFilterOption,
} from '@/types';
// eslint-disable-next-line import/no-cycle
import router from '@/router';
import { UserState } from '@/composables/User';
import { formatMysqlDateToLocale } from '@/helpers/Date';
import Badge from 'primevue/badge';
import Tooltip from 'primevue/tooltip';
import Button from 'primevue/button';
import CustomSchemaEditorModal from '@/components/custom-data/CustomSchemaEditorModal.vue';
import { FilterOperatorsEnum } from '@/types/enums';
import {
  fetchAllSchemasData, SchemaType, SchemaItem, activateCustomSchema, deactivateCustomSchema, deleteCustomSchema,
} from '@/composables/custom-data/customData';
import { useConfirm } from 'primevue/useconfirm';
import ConfirmPopup from 'primevue/confirmpopup';

export default defineComponent({
  name: 'SyncList',
  components: {
    SpmTable,
    Badge,
    Button,
    CustomSchemaEditorModal,
    ConfirmPopup,
  },

  directives: {
    tooltip: Tooltip,
  },

  setup() {
    const confirm = useConfirm();
    const { t, locale } = useI18n();
    // We are only interested with "selectedItems" here, unless "items" are to be transformed ...
    const tableData = ref<SpmTableState>({
      items: [],
      total: 1,
      error: '',
      selectedItems: [],
      isLoading: true,
    });

    const isCustomObjectModalOpen = ref(false);
    const currentCustomDataId = ref<number|null>(null);
    const tableDataRefKey = ref(0);
    const actions: SpmTableAction[] = [
      {
        icon: 'far fa-eye',
        label: t('myShopData.syncs.viewData'),
        show: () => true,
        callback: (sync: SchemaItem) => {
          const routeConfig: Record<string, any> = {
            name: `shop.syncs.${sync.type}`,
          };

          if (sync.schema_type === SchemaType.Custom && sync.id_custom_object) {
            routeConfig.query = { id: sync.id_custom_object };
          }
          // For system types, ensure navigation is correctly handled if 'shop.syncs.[system_schema_name]' exists
          // or adjust routeConfig.name accordingly if system schemas have a different naming pattern for routes.
          router.push(routeConfig);
        },
      },
      {
        icon: 'fas fa-pencil-alt',
        label: t('customData.syncList.editObject'),
        show: (sync: SchemaItem) => sync.schema_type === SchemaType.Custom,
        callback: (sync: SchemaItem) => {
          currentCustomDataId.value = sync.id_custom_object ? Number(sync.id_custom_object) : null;
          isCustomObjectModalOpen.value = true;
        },
      },
      {
        icon: 'fas fa-play-circle',
        label: t('myShopData.actions.activate'),
        show: (sync: SchemaItem) => sync.schema_type === SchemaType.Custom && sync.active === false,
        callback: (sync: SchemaItem) => {
          // Call API to activate the custom schema
          if (!sync.id_custom_object) return;
          activateCustomSchema(sync.id_custom_object).then(async (success) => {
            if (success) {
              // Refresh table data after activation
              tableData.value.items = await fetchAllSchemasData(UserState.activeShop?.id ?? 0);
              tableDataRefKey.value += 1;
            }
          });
        },
      },
      {
        icon: 'fas fa-pause-circle',
        label: t('myShopData.actions.deactivate'),
        show: (sync: SchemaItem) => sync.schema_type === SchemaType.Custom && sync.active === true,
        callback: (sync: SchemaItem) => {
          // Call API to deactivate the custom schema
          if (!sync.id_custom_object) return;
          deactivateCustomSchema(sync.id_custom_object).then(async (success) => {
            if (success) {
              // Refresh table data after deactivation
              tableData.value.items = await fetchAllSchemasData(UserState.activeShop?.id ?? 0);
              tableDataRefKey.value += 1;
            }
          });
        },
      },
      {
        icon: 'far fa-trash-alt',
        label: t('myShopData.actions.delete'),
        show: (sync: SchemaItem) => sync.schema_type === SchemaType.Custom,
        callback: (sync: SchemaItem, event: Event) => {
          if (!sync.id_custom_object) return;
          confirm.require({
            group: 'basicGroup',
            target: event?.currentTarget instanceof HTMLElement ? event.currentTarget : undefined,
            message: t('customData.syncList.confirmDelete'),
            header: t('confirmation'),
            icon: 'far fa-exclamation-triangle',
            acceptLabel: t('yes'),
            rejectLabel: t('no'),
            acceptClass: 'p-button-danger',
            rejectClass: 'p-button-secondary',
            accept: async () => {
              const id = sync.id_custom_object as number;
              const success = await deleteCustomSchema(id);
              if (success) {
                tableData.value.items = await fetchAllSchemasData(UserState.activeShop?.id ?? 0);
                tableDataRefKey.value += 1;
              }
            },
          });
        },
      },
    ];

    const schemaTypeOptions: SpmTableFilterOption[] = [
      {
        label: t('myShopData.schemaType.system'),
        value: SchemaType.System,
      },
      {
        label: t('myShopData.schemaType.custom'),
        value: SchemaType.Custom,
      },
    ];

    const columns: SpmTableColumns[] = [
      {
        field: 'type',
        header: '',
        sortable: false,
        filterable: true,
        editable: false,
        style: '',
        type: 'text',
        hidden: true,
      },
      {
        field: 'schema_name',
        header: t('myShopData.syncs.schema_name'),
        sortable: false,
        filterable: true,
        editable: false,
        style: '',
        type: 'text',
      },
      {
        field: 'schema_type',
        header: t('myShopData.syncs.schema_type'),
        sortable: false,
        filterable: true,
        editable: false,
        style: '',
        type: 'text',
        filterSettings: {
          type: 'multiSelect',
          options: schemaTypeOptions,
          hideFilterMenu: true,
          showFilterInput: false,
          defaultMatchMode: FilterOperatorsEnum.IN,
        },
      },
      {
        field: 'status',
        header: t('myShopData.syncs.status'),
        sortable: false,
        filterable: true,
        editable: false,
        style: 'width:10%',
        type: 'text',
      },
      {
        field: '',
        header: t('templates.actions'),
        sortable: false,
        filterable: false,
        editable: false,
        style: 'width:10%',
        type: 'action',
        actions,
      },
    ];

    onBeforeMount(async () => {
      tableData.value.items = await fetchAllSchemasData(UserState.activeShop?.id ?? 0);
      tableData.value.isLoading = false;
    });

    const toggleCustomSchemaModal = async () => {
      if (isCustomObjectModalOpen.value) {
        tableData.value.items = await fetchAllSchemasData(UserState.activeShop?.id ?? 0);
        tableDataRefKey.value += 1;
      }
      isCustomObjectModalOpen.value = !isCustomObjectModalOpen.value;
      if (!isCustomObjectModalOpen.value) currentCustomDataId.value = null;
    };

    return {
      t,
      columns,
      tableData,
      isCustomObjectModalOpen,
      toggleCustomSchemaModal,
      currentCustomDataId,
      locale,
      formatMysqlDateToLocale,
      tableDataRefKey,
    };
  },
});
